/**
 * Server-driven wizard provider.
 *
 * Wraps `useWizardInstance` and provides the SAME context shape as
 * `OnboardingWizardProvider`, so all step components (SplashStep,
 * FormStep, PollingStep, etc.) work unchanged.
 *
 * Props:
 *   - instanceId: The wizard instance ID from the URL
 *   - children: The wizard UI tree (typically <OnboardingWizard />)
 */

import { ReactNode, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWizardInstance } from "../../hooks/useWizardInstance";
import { adaptServerState } from "../../lib/wizardAdapter";
import { useGamification } from "../../hooks/useGamification";
import { useUserXp } from "../../hooks/useUserXp";
import {
  OnboardingWizardContext,
  type OnboardingWizardContextType,
} from "../onboarding/WizardContext";
import type { StepAction } from "../../data/onboardingConfig.types";
import type {
  WizardActionContext,
  WizardActionResult,
} from "../../hooks/useOnboardingActions";
import { Loader2, AlertCircle } from "lucide-react";

interface ServerDrivenWizardProviderProps {
  instanceId: string;
  children: ReactNode;
}

export function ServerDrivenWizardProvider({
  instanceId,
  children,
}: ServerDrivenWizardProviderProps) {
  const navigate = useNavigate();
  const {
    wizardState,
    isLoading,
    error,
    effectiveStepIndex,
    localFormData,
    updateLocalFormData,
    completeStep,
    goNext: hookGoNext,
    goBack: hookGoBack,
  } = useWizardInstance(instanceId);

  // Track completed steps locally for gamification/UI (supplements server state)
  const [localCompletedSteps, setLocalCompletedSteps] = useState<boolean[]>([]);

  // Error state first: a failed load has no state, so the loading check below would spin forever
  if (error) {
    return (
      <div className="min-h-[100dvh] bg-muted flex flex-col items-center justify-center gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-destructive text-lg font-medium">
          {error.message || "Failed to load wizard"}
        </p>
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2 text-sm font-medium text-nara-orange hover:underline"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  // Loading state
  if (isLoading || !wizardState) {
    return (
      <div className="min-h-[100dvh] bg-muted flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-nara-orange" />
      </div>
    );
  }

  // Clean up old localStorage onboarding key when wizard completes
  if (wizardState.status === "completed") {
    localStorage.removeItem("narahub-onboarding-new-user");
  }

  // Adapt server state to the shape step components expect
  const adapted = adaptServerState(wizardState);
  const { config } = adapted;

  return (
    <ServerDrivenWizardProviderInner
      adapted={adapted}
      config={config}
      effectiveStepIndex={effectiveStepIndex}
      localFormData={localFormData}
      localCompletedSteps={localCompletedSteps}
      updateLocalFormData={updateLocalFormData}
      setLocalCompletedSteps={setLocalCompletedSteps}
      completeStep={completeStep}
      goNext={hookGoNext}
      goBack={hookGoBack}
      navigate={navigate}
    >
      {children}
    </ServerDrivenWizardProviderInner>
  );
}

/**
 * Inner component that uses hooks (useGamification needs to be called
 * unconditionally, so we separate it from the conditional loading/error returns).
 */
function ServerDrivenWizardProviderInner({
  adapted,
  config,
  effectiveStepIndex,
  localFormData,
  localCompletedSteps,
  updateLocalFormData,
  setLocalCompletedSteps,
  completeStep,
  goNext,
  goBack,
  navigate,
  children,
}: {
  adapted: ReturnType<typeof adaptServerState>;
  config: ReturnType<typeof adaptServerState>["config"];
  effectiveStepIndex: number;
  localFormData: Record<string, string>;
  localCompletedSteps: boolean[];
  updateLocalFormData: (data: Record<string, string>) => void;
  setLocalCompletedSteps: React.Dispatch<React.SetStateAction<boolean[]>>;
  completeStep: (stepId: string, data: Record<string, any>) => Promise<any>;
  goNext: () => void;
  goBack: () => void;
  navigate: ReturnType<typeof useNavigate>;
  children: ReactNode;
}) {
  const gamification = useGamification(config);
  const { data: userXpData } = useUserXp();

  // Override totalPoints with server-side XP when available
  const serverTotalPoints = userXpData?.total_xp ?? gamification.totalPoints;

  // Merge server completed steps with local tracking
  const mergedCompletedSteps = adapted.completedSteps.map(
    (serverComplete, i) => serverComplete || (localCompletedSteps[i] ?? false)
  );

  const totalSteps = adapted.steps.length;
  const stepConfig = adapted.steps[effectiveStepIndex];

  // Merge form data: server collected_data + local accumulations
  const mergedFormData: Record<string, string> = {
    ...adapted.formData,
    ...localFormData,
  };

  const markStepComplete = useCallback(
    (index: number) => {
      setLocalCompletedSteps((prev) => {
        const next = [...prev];
        while (next.length <= index) next.push(false);
        next[index] = true;
        return next;
      });
    },
    [setLocalCompletedSteps]
  );

  // Action executor: routes to server completeStep for real actions,
  // handles goToDashboard client-side
  const executeAction = useCallback(
    async (
      action: StepAction,
      context: WizardActionContext
    ): Promise<WizardActionResult> => {
      try {
        // Client-side overrides
        if (action.type === "goToDashboard") {
          navigate("/");
          return { success: true };
        }

        // For polling actions (checkProjectDocuments, etc.), delegate to server
        // by completing the step with the current form data
        const currentStepId = adapted.steps[effectiveStepIndex]?.id;
        if (!currentStepId) {
          return { success: false, error: "No current step" };
        }

        const result = await completeStep(currentStepId, context.formData);
        return { success: true, data: result, serverAdvanced: true };
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return { success: false, error: message };
      }
    },
    [navigate, completeStep, adapted.steps, effectiveStepIndex]
  );

  const value: OnboardingWizardContextType = {
    // State
    currentStep: effectiveStepIndex,
    stepConfig,
    formData: mergedFormData,
    completedSteps: mergedCompletedSteps,
    isFirstStep: effectiveStepIndex === 0,
    isLastStep: effectiveStepIndex === totalSteps - 1,
    totalSteps,
    // Navigation
    goNext,
    goBack,
    updateFormData: updateLocalFormData,
    markStepComplete,
    // Actions
    executeAction,
    // Gamification
    ...gamification,
    totalPoints: serverTotalPoints,
    // Config
    config,
  };

  return (
    <OnboardingWizardContext.Provider value={value}>
      {children}
    </OnboardingWizardContext.Provider>
  );
}
